import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { createWorker } from '@/core/worker/workerManager';

const WORKER_STORE_DEBUG_PREFIX = '[PSDBG][worker-store]';

/**
 * Worker Store
 * Manages worker loading state and instance
 *
 * 契约（方案 §5.1 / §5.2 / §5.3）：
 * - preloadWorker() 返回本次共享的 promise；加载失败时 reject（不再吞错）；
 * - 并发 preload 只创建一个 Worker 并共享同一结果；
 * - 成功后立即 resolve 已有实例；失败后调用方可 .catch 收到失败；
 * - 用代次令牌（preloadGeneration）防止 reset 后旧 promise 的迟到成功把 Store 重新写成 ready。
 */
export const useWorkerStore = defineStore('worker', () => {
    // State
    const isLoaded = ref(false);
    const isLoading = ref(false);
    const error = ref(null);
    const workerInstance = ref(null);

    // 共享 in-flight promise：并发 preload 只创建一个 Worker 并共享同一结果（§5.2#1-2）
    const preloadPromise = ref(null);
    // 代次令牌：reset / 新建 Worker 时递增，迟到回调校验代次后失效（§5.2#6 / §5.3#5）
    const preloadGeneration = ref(0);

    // Getters
    const isReady = computed(() => isLoaded.value && !error.value);

    function invalidateInflight() {
        preloadGeneration.value++;
        preloadPromise.value = null;
    }

    async function preloadWorker() {
        // 已成功：直接 resolve 已有实例（§5.1 / §5.2#4）
        if (isLoaded.value && workerInstance.value) {
            return workerInstance.value;
        }
        // 加载中：返回共享的 in-flight promise，不重复创建 Worker（§5.2#1-2）
        if (preloadPromise.value) {
            return preloadPromise.value;
        }

        isLoading.value = true;
        error.value = null;
        const generation = ++preloadGeneration.value;

        const run = async () => {
            const worker = await createWorker({ purpose: 'result-main-preload' });
            // 迟到守卫：若期间发生 reset 或新一轮 preload，丢弃本次结果并终止孤儿 Worker
            if (generation !== preloadGeneration.value) {
                worker.terminate();
                return;
            }
            workerInstance.value = worker;
            isLoaded.value = true;
            console.log(`${WORKER_STORE_DEBUG_PREFIX} preload success`);
            return worker;
        };

        const promise = run()
            .catch((err) => {
                // 统一失败传播（§5.1）：promise reject，Store 同时写 error
                console.error(`${WORKER_STORE_DEBUG_PREFIX} preload failed`, {
                    message: err.message
                });
                error.value = err.message;
                if (workerInstance.value) {
                    workerInstance.value.terminate();
                    workerInstance.value = null;
                }
                // 重新抛出，让调用方 .catch 能收到失败（不再吞错）
                throw err;
            })
            .finally(() => {
                // 仅当本次仍是当前代次时才收尾 loading 状态
                if (generation === preloadGeneration.value) {
                    isLoading.value = false;
                }
                // 不论成功失败，本代次 promise 已 settle，清空 in-flight（§5.2#5）
                if (preloadPromise.value === promise) {
                    preloadPromise.value = null;
                }
            });

        // 缓存当前代次的共享 promise
        preloadPromise.value = promise;
        return promise;
    }

    function setWorkerInstance(worker) {
        // 手动接管一个已就绪 Worker：作废任何在途 preload，避免孤儿 Worker 覆盖本实例
        invalidateInflight();
        workerInstance.value = worker;
        isLoaded.value = true;
        isLoading.value = false;
        error.value = null;
    }

    function clearWorker() {
        // 清除实例的同时作废在途 promise，使迟到回调失效
        invalidateInflight();
        if (workerInstance.value) {
            try {
                workerInstance.value.terminate();
            } catch (e) {
                // Ignore if already terminated
            }
            workerInstance.value = null;
        }
        isLoaded.value = false;
        isLoading.value = false;
        error.value = null;
    }

    function reset() {
        clearWorker();
    }

    return {
        // State
        isLoaded,
        isLoading,
        error,
        workerInstance,

        // Getters
        isReady,

        // Actions
        preloadWorker,
        setWorkerInstance,
        clearWorker,
        reset
    };
});
